import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest } from '../../api/client';
import '../auth.css';
import './invite.css';

type InviteValidation = {
  valid: true;
  name: string;
  email: string;
  role: 'admin' | 'technician';
  tenantName: string;
  logoBase64: string | null;
};

type PageState = 'loading' | 'review' | 'decline_confirm' | 'accepted' | 'declined';

function roleLabel(role: string): string {
  return role === 'admin' ? 'Admin' : 'Technician';
}

export default function InvitePage(): React.JSX.Element {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [invite, setInvite] = useState<InviteValidation | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await apiRequest<InviteValidation>(`/api/invitations/validate/${token}`);
        if (cancelled) return;
        if (!res.valid) {
          navigate('/invite/expired', { replace: true });
          return;
        }
        setInvite(res);
        setPageState('review');
      } catch {
        if (!cancelled) navigate('/invite/expired', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, token]);

  const handleAccept = async () => {
    setAccepting(true);
    setActionError(null);
    try {
      await apiRequest(`/api/invitations/accept/${token}`, { method: 'POST' });
      setPageState('accepted');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Could not accept invitation.';
      if (message.includes('404') || message.includes('410')) {
        navigate('/invite/expired', { replace: true });
        return;
      }
      setActionError(message.replace(/^HTTP \d+[^:]+:\s*/, ''));
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineConfirm = async () => {
    setDeclining(true);
    setActionError(null);
    try {
      await apiRequest(`/api/invitations/deny/${token}`, { method: 'POST' });
      setPageState('declined');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Could not decline invitation.';
      if (message.includes('404') || message.includes('410')) {
        navigate('/invite/expired', { replace: true });
        return;
      }
      setActionError(message.replace(/^HTTP \d+[^:]+:\s*/, ''));
    } finally {
      setDeclining(false);
    }
  };

  const logoSrc = invite?.logoBase64 || '/logo.svg';

  return (
    <AppShell showNav={false} showFooter={false}>
      <div className="invite-page">
        <section className="invite-card">
          {pageState === 'loading' ? (
            <div className="invite-loading">
              <div className="invite-loading-spinner" aria-hidden />
              <p>Loading invitation…</p>
            </div>
          ) : null}

          {pageState === 'review' && invite ? (
            <>
              <img src={logoSrc} alt="" className="invite-logo" />
              <h1 className="invite-title">You&apos;ve been invited to join {invite.tenantName}</h1>
              <p className="invite-body">
                Hi {invite.name}, you&apos;ve been invited to join as a {roleLabel(invite.role)}.
              </p>
              {actionError ? <p className="invite-error">{actionError}</p> : null}
              <div className="invite-actions">
                <button
                  type="button"
                  className="btn-primary invite-btn-accept"
                  disabled={accepting || declining}
                  onClick={() => void handleAccept()}
                >
                  {accepting ? 'Creating account…' : 'Accept & Create Account'}
                </button>
                <button
                  type="button"
                  className="btn-secondary invite-btn-decline"
                  disabled={accepting || declining}
                  onClick={() => setPageState('decline_confirm')}
                >
                  Decline
                </button>
              </div>
            </>
          ) : null}

          {pageState === 'decline_confirm' ? (
            <>
              <h1 className="invite-title">Decline invitation?</h1>
              <p className="invite-body">Are you sure you want to decline this invitation?</p>
              {actionError ? <p className="invite-error">{actionError}</p> : null}
              <div className="invite-actions">
                <button
                  type="button"
                  className="btn-secondary invite-btn-decline"
                  disabled={declining}
                  onClick={() => void handleDeclineConfirm()}
                >
                  {declining ? 'Declining…' : 'Yes, decline'}
                </button>
                <button
                  type="button"
                  className="settings-text-btn"
                  disabled={declining}
                  onClick={() => setPageState('review')}
                >
                  Go back
                </button>
              </div>
            </>
          ) : null}

          {pageState === 'accepted' ? (
            <>
              <h1 className="invite-title">You&apos;re in!</h1>
              <p className="invite-body">
                Your account has been created. Check your email for your login credentials. You can
                close this page.
              </p>
            </>
          ) : null}

          {pageState === 'declined' ? (
            <>
              <h1 className="invite-title">Invitation declined.</h1>
              <p className="invite-body">
                You have declined the invitation. If this was a mistake, contact your administrator.
              </p>
            </>
          ) : null}
        </section>
      </div>
    </AppShell>
  );
}
